import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import MediaField from "../../_components/MediaField";
import { emptyStage } from "../utils/route.utils";

const RouteForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Save Route",
  submittingLabel = "Saving...",
  readOnly = false,
  rallies = [],
  isRalliesLoading = false,
  onSubmit,
  onFieldChange,
  onBack,
}) => {
  const disabled = submitting || readOnly;
  const stages = form.stages || [];

  const updateStage = (index, patch) => {
    const next = stages.map((stage, i) =>
      i === index ? { ...stage, ...patch } : stage
    );
    onFieldChange("stages", next);
  };

  const addStage = () => {
    onFieldChange("stages", [...stages, emptyStage()]);
  };

  const removeStage = (index) => {
    const next = stages.filter((_, i) => i !== index);
    onFieldChange("stages", next);
  };

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder="Main Desert Route"
              isInvalid={!!validationErrors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Total Distance (km) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.1"
              value={form.total_distance_km}
              onChange={(e) =>
                onFieldChange("total_distance_km", e.target.value)
              }
              placeholder="350"
              isInvalid={!!validationErrors.total_distance_km}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.total_distance_km}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Estimated Time</Form.Label>
            <Form.Control
              type="text"
              value={form.estimated_time}
              onChange={(e) => onFieldChange("estimated_time", e.target.value)}
              placeholder="8-10 Hours"
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Main competitive route across the desert."
              disabled={disabled}
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Track Map</h5>
      <MediaField
        label="Track Map Image"
        value={form.track_map_image || ""}
        disabled={disabled}
        error={validationErrors.track_map_image}
        onChange={(next) => onFieldChange("track_map_image", next)}
      />

      <h5 className="mb-2 mt-3">Stages</h5>
      <p className="text-muted small mb-3">
        Optional. Each stage needs a unique stage number. Rally (event) can be
        attached now or left empty and set later. Checkpoints are managed
        separately after the route exists.
      </p>

      {stages.length === 0 ? (
        <p className="text-muted mb-3">No stages added yet.</p>
      ) : (
        stages.map((stage, index) => {
          const stageError = validationErrors.stages?.[index] || {};

          return (
            <div
              key={stage._id || `stage-${index}`}
              className="border rounded p-3 mb-3"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Stage {index + 1}</h6>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  disabled={disabled}
                  onClick={() => removeStage(index)}
                >
                  Remove
                </Button>
              </div>

              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Stage Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={stage.stage_number}
                      onChange={(e) =>
                        updateStage(index, { stage_number: e.target.value })
                      }
                      placeholder="1"
                      isInvalid={!!stageError.stage_number}
                      disabled={disabled}
                    />
                    <Form.Control.Feedback type="invalid">
                      {stageError.stage_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rally (optional)</Form.Label>
                    <Form.Select
                      value={stage.event_id || ""}
                      onChange={(e) =>
                        updateStage(index, { event_id: e.target.value })
                      }
                      disabled={disabled || isRalliesLoading}
                    >
                      <option value="">Attach later</option>
                      {rallies.map((rally) => {
                        const rallyId = rally._id || rally.id;
                        return (
                          <option key={rallyId} value={rallyId}>
                            {rally.name || rally.title || rallyId}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Leave empty to create the stage without a rally.
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stage Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={stage.stage_name}
                      onChange={(e) =>
                        updateStage(index, { stage_name: e.target.value })
                      }
                      placeholder="Inaugural Stage"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={stage.date}
                      onChange={(e) =>
                        updateStage(index, { date: e.target.value })
                      }
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Time Start</Form.Label>
                    <Form.Control
                      type="time"
                      value={stage.time_start}
                      onChange={(e) =>
                        updateStage(index, { time_start: e.target.value })
                      }
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Time End</Form.Label>
                    <Form.Control
                      type="time"
                      value={stage.time_end}
                      onChange={(e) =>
                        updateStage(index, { time_end: e.target.value })
                      }
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Starting Location</Form.Label>
                    <Form.Control
                      type="text"
                      value={stage.starting_location}
                      onChange={(e) =>
                        updateStage(index, {
                          starting_location: e.target.value,
                        })
                      }
                      placeholder="Derawar Fort"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ending Location</Form.Label>
                    <Form.Control
                      type="text"
                      value={stage.ending_location}
                      onChange={(e) =>
                        updateStage(index, {
                          ending_location: e.target.value,
                        })
                      }
                      placeholder="Yazman"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label>Stage Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={stage.stage_description}
                      onChange={(e) =>
                        updateStage(index, {
                          stage_description: e.target.value,
                        })
                      }
                      placeholder="Opening desert leg"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline-primary"
        className="mb-4"
        disabled={disabled}
        onClick={addStage}
      >
        + Add Stage
      </Button>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RouteForm;
